import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Calculator, Mail, Users, Globe, DollarSign } from 'lucide-react';

const CostCalculator = () => {
  const [emailVolume, setEmailVolume] = useState(1000000);
  const [emailUnitCost, setEmailUnitCost] = useState(0.00150);
  const [uamUsers, setUamUsers] = useState(10000);
  const [uamUnitCost, setUamUnitCost] = useState(0.055);
  const [ipCount, setIpCount] = useState(2);
  const [ipUnitCost, setIpUnitCost] = useState(200);
  
  const [totals, setTotals] = useState({
    emailTotal: 0,
    uamTotal: 0,
    ipTotal: 0,
    grandTotal: 0
  });

  // Store display values separately from calculation values
  const [displayValues, setDisplayValues] = useState({
    emailVolume: '1.000.000',
    emailUnitCost: '0,00150',
    uamUsers: '10.000',
    uamUnitCost: '0,055',
    ipCount: '2',
    ipUnitCost: '200'
  });

  const parseValue = (value) => {
    if (!value || value === '') return 0;
    // Convert comma to dot and remove any spaces
    const cleanValue = value.toString().replace(',', '.').replace(/\s/g, '');
    const num = parseFloat(cleanValue);
    return isNaN(num) ? 0 : num;
  };

  const formatVolumeNumber = (value) => {
    if (!value || value === '' || value === '0') return '';
    const num = parseFloat(value.toString().replace(',', '.'));
    if (isNaN(num)) return value;
    return num.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
  };

  const handleInputChange = (field, value, setter) => {
    // Update display value (what user sees)
    setDisplayValues(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Update calculation value (parsed number)
    const numericValue = parseValue(value);
    setter(numericValue);
  };

  const handleVolumeBlur = (field, value) => {
    // Format volume/count fields when user finishes typing
    const isVolumeField = field === 'emailVolume' || field === 'uamUsers' || field === 'ipCount';
    
    if (isVolumeField && value && value !== '') {
      const formattedValue = formatVolumeNumber(value);
      setDisplayValues(prev => ({
        ...prev,
        [field]: formattedValue
      }));
    }
  };

  useEffect(() => {
    const emailTotal = emailVolume * emailUnitCost;
    const uamTotal = uamUsers * uamUnitCost;
    const ipTotal = ipCount * ipUnitCost;
    const grandTotal = emailTotal + uamTotal + ipTotal;
    
    setTotals({
      emailTotal,
      uamTotal,
      ipTotal,
      grandTotal
    });
  }, [emailVolume, emailUnitCost, uamUsers, uamUnitCost, ipCount, ipUnitCost]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Calculator className="w-8 h-8 text-slate-700 mr-3" />
            <h1 className="text-4xl font-bold text-slate-800">System Cost Calculator</h1>
          </div>
          <p className="text-slate-600 text-lg">Calculate your monthly system costs with precision</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Email Cost Section */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center text-slate-700">
                <Mail className="w-5 h-5 mr-2 text-blue-600" />
                Email Service
              </CardTitle>
              <CardDescription>Calculate costs based on monthly email volume</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="emailVolume" className="text-sm font-medium text-slate-700">
                  Monthly Email Volume
                </Label>
                <Input
                  id="emailVolume"
                  type="text"
                  placeholder="e.g., 10.000"
                  value={displayValues.emailVolume}
                  onChange={(e) => handleInputChange('emailVolume', e.target.value, setEmailVolume)}
                  onBlur={() => handleVolumeBlur('emailVolume', displayValues.emailVolume)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="emailUnitCost" className="text-sm font-medium text-slate-700">
                  Cost per Email (R$)
                </Label>
                <Input
                  id="emailUnitCost"
                  type="text"
                  placeholder="e.g., 0,0012"
                  value={displayValues.emailUnitCost}
                  onChange={(e) => handleInputChange('emailUnitCost', e.target.value, setEmailUnitCost)}
                  className="mt-1"
                />
              </div>
              <div className="pt-2 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Monthly Total:</span>
                  <span className="text-lg font-semibold text-blue-600">
                    {formatCurrency(totals.emailTotal)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* UAM Cost Section */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center text-slate-700">
                <Users className="w-5 h-5 mr-2 text-green-600" />
                User Activity Monitoring
              </CardTitle>
              <CardDescription>Calculate UAM costs based on monitored users</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="uamUsers" className="text-sm font-medium text-slate-700">
                  Number of Monitored Users
                </Label>
                <Input
                  id="uamUsers"
                  type="text"
                  placeholder="e.g., 500"
                  value={displayValues.uamUsers}
                  onChange={(e) => handleInputChange('uamUsers', e.target.value, setUamUsers)}
                  onBlur={() => handleVolumeBlur('uamUsers', displayValues.uamUsers)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="uamUnitCost" className="text-sm font-medium text-slate-700">
                  Cost per User per Month (R$)
                </Label>
                <Input
                  id="uamUnitCost"
                  type="text"
                  placeholder="e.g., 2,50"
                  value={displayValues.uamUnitCost}
                  onChange={(e) => handleInputChange('uamUnitCost', e.target.value, setUamUnitCost)}
                  className="mt-1"
                />
              </div>
              <div className="pt-2 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Monthly Total:</span>
                  <span className="text-lg font-semibold text-green-600">
                    {formatCurrency(totals.uamTotal)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* IP Cost Section */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center text-slate-700">
                <Globe className="w-5 h-5 mr-2 text-purple-600" />
                IP Addresses
              </CardTitle>
              <CardDescription>Calculate costs for dedicated IP addresses</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="ipCount" className="text-sm font-medium text-slate-700">
                  Number of IP Addresses
                </Label>
                <Input
                  id="ipCount"
                  type="text"
                  placeholder="e.g., 3"
                  value={displayValues.ipCount}
                  onChange={(e) => handleInputChange('ipCount', e.target.value, setIpCount)}
                  onBlur={() => handleVolumeBlur('ipCount', displayValues.ipCount)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="ipUnitCost" className="text-sm font-medium text-slate-700">
                  Cost per IP per Month (R$)
                </Label>
                <Input
                  id="ipUnitCost"
                  type="text"
                  placeholder="e.g., 15,00"
                  value={displayValues.ipUnitCost}
                  onChange={(e) => handleInputChange('ipUnitCost', e.target.value, setIpUnitCost)}
                  className="mt-1"
                />
              </div>
              <div className="pt-2 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Monthly Total:</span>
                  <span className="text-lg font-semibold text-purple-600">
                    {formatCurrency(totals.ipTotal)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Section */}
          <Card className="border-0 shadow-xl bg-gradient-to-r from-slate-800 to-slate-700 text-white">
            <CardHeader>
              <CardTitle className="flex items-center">
                <DollarSign className="w-5 h-5 mr-2" />
                Cost Summary
              </CardTitle>
              <CardDescription className="text-slate-300">
                Your total monthly system costs
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-300">Email Service:</span>
                  <span className="font-medium">{formatCurrency(totals.emailTotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">UAM Service:</span>
                  <span className="font-medium">{formatCurrency(totals.uamTotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">IP Addresses:</span>
                  <span className="font-medium">{formatCurrency(totals.ipTotal)}</span>
                </div>
                <Separator className="bg-slate-600" />
                <div className="flex justify-between text-xl font-bold">
                  <span>Total Monthly Cost:</span>
                  <span className="text-yellow-400">{formatCurrency(totals.grandTotal)}</span>
                </div>
              </div>
              
              {totals.grandTotal > 0 && (
                <div className="mt-6 p-4 bg-slate-700/50 rounded-lg">
                  <div className="text-sm text-slate-300 mb-2">Annual Projection:</div>
                  <div className="text-2xl font-bold text-yellow-400">
                    {formatCurrency(totals.grandTotal * 12)}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Usage Statistics */}
        {(emailVolume > 0 || uamUsers > 0 || ipCount > 0) && (
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-slate-700">Usage Statistics</CardTitle>
              <CardDescription>Overview of your system usage</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {formatNumber(emailVolume)}
                  </div>
                  <div className="text-sm text-slate-600">Emails per Month</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {formatNumber(uamUsers)}
                  </div>
                  <div className="text-sm text-slate-600">Monitored Users</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {formatNumber(ipCount)}
                  </div>
                  <div className="text-sm text-slate-600">IP Addresses</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CostCalculator;